import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Radio, ShieldCheck, FileText, Newspaper, CalendarDays, BarChart3, Bell,
  ArrowRight, Loader2, PlayCircle, CheckCircle2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (p) => `${BACKEND}/marketing/${p}`;

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};
const inView = { initial: "hidden", whileInView: "show", viewport: { once: true, amount: 0.25 } };

const FEATURES = [
  { icon: ShieldCheck, title: "Leadership-first approval", body: "Every request is authorized by leadership before any work begins — your brand voice stays consistent." },
  { icon: FileText, title: "Guided submissions", body: "Simple, guided forms for communications requests and weekly announcements. No more scattered Google Forms." },
  { icon: Newspaper, title: "Newsletter Builder", body: "Assemble approved announcements into a polished, ready-to-send newsletter in minutes." },
  { icon: CalendarDays, title: "Shared calendar", body: "Every scheduled publication and event in one clear view, so the whole team stays aligned." },
  { icon: BarChart3, title: "Reports & insights", body: "See volume, workload, and turnaround time across every team at a glance." },
  { icon: Bell, title: "Automatic notifications", body: "Requesters always know where things stand — from submitted, to approved, to published." },
];

const SHOTS = [
  ["comms_dashboard_neutral.png", "Production dashboard"],
  ["issue_builder_neutral.png", "Newsletter builder"],
  ["reports_neutral.png", "Reports & insights"],
  ["calendar_neutral.png", "Shared calendar"],
];

export default function Home() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [narrated, setNarrated] = useState(true);

  const startDemo = async () => {
    setLoadingDemo(true);
    try {
      localStorage.setItem("cc_skin", "neutral");
      document.documentElement.setAttribute("data-skin", "neutral");
      localStorage.removeItem("cc_tour_done");
      const { data } = await api.post("/auth/demo-login");
      login(data.user, data.token);
      navigate("/admin");
    } catch (e) {
      localStorage.setItem("cc_skin", "aka");
      document.documentElement.setAttribute("data-skin", "aka");
      setLoadingDemo(false);
      toast.error("Could not start the demo. Please try again.");
    }
  };

  const memberLogin = () => {
    localStorage.setItem("cc_skin", "aka");
    document.documentElement.setAttribute("data-skin", "aka");
    navigate("/AEGO");
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#252A31]" data-testid="home-landing">
      {/* Nav */}
      <nav className="sticky top-0 z-20 flex items-center justify-between border-b border-[#DDE2E7] bg-[#F7F8FA]/85 px-6 py-4 backdrop-blur-xl lg:px-12">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#243447] text-white">
            <Radio className="h-5 w-5" />
          </div>
          <span className="text-lg font-bold tracking-tight text-[#243447]">CommConnect</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => navigate("/pricing")} data-testid="home-pricing-nav"
            className="rounded-full px-4 py-2 text-sm font-semibold text-[#243447] transition hover:bg-[#E8EEF4]">
            Pricing
          </button>
          <button onClick={memberLogin} data-testid="home-member-login"
            className="rounded-full px-4 py-2 text-sm font-semibold text-[#243447] transition hover:bg-[#E8EEF4]">
            Member Login
          </button>
          <button onClick={startDemo} disabled={loadingDemo} data-testid="home-demo-btn-nav"
            className="flex items-center gap-2 rounded-full bg-[#243447] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#2E4056] disabled:opacity-60">
            {loadingDemo ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            View Live Demo
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-16 pb-10 lg:px-12">
        <div className="cc-aurora" aria-hidden="true">
          <span className="a1" /><span className="a2" /><span className="a3" />
        </div>
        <motion.div variants={stagger} initial="hidden" animate="show" className="relative z-10 mx-auto max-w-5xl text-center">
          <motion.span variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-[#DDE2E7] bg-[#E8EEF4] px-4 py-1.5 text-xs font-medium text-[#5F7FA3]">
            <CheckCircle2 className="h-3.5 w-3.5" /> Communications management for volunteer-led organizations
          </motion.span>
          <motion.h1 variants={fadeUp} className="mx-auto mt-6 max-w-4xl text-4xl font-bold leading-tight tracking-tight text-[#243447] sm:text-5xl lg:text-6xl">
            One portal. Every request.<br /><span className="text-[#5F7FA3]">Clearer communication.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mx-auto mt-6 max-w-2xl text-lg text-[#565E6B]">
            CommConnect replaces scattered forms and endless follow-ups with a single, guided system —
            from member submissions and leadership approvals to newsletter production and scheduling.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={startDemo} disabled={loadingDemo} data-testid="home-demo-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#243447] px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#243447]/15 transition hover:bg-[#2E4056] disabled:opacity-60 sm:w-auto">
              {loadingDemo ? <Loader2 className="h-5 w-5 animate-spin" /> : <PlayCircle className="h-5 w-5" />}
              Explore the Live Demo
            </button>
            <button onClick={memberLogin} data-testid="home-member-login-hero"
              className="flex w-full items-center justify-center gap-2 rounded-full border border-[#DDE2E7] bg-white px-8 py-3.5 text-base font-semibold text-[#243447] transition hover:bg-[#E8EEF4] sm:w-auto">
              Member Login <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
          <motion.p variants={fadeUp} className="mt-4 text-sm text-[#8A93A0]">No sign-up needed — the demo opens instantly (read-only).</motion.p>
        </motion.div>
      </section>

      {/* Features */}
      <section className="px-6 pb-20 pt-4 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <motion.h2 variants={fadeUp} {...inView} className="text-center text-3xl font-bold tracking-tight text-[#243447]">Everything in one place</motion.h2>
          <motion.p variants={fadeUp} {...inView} className="mx-auto mt-3 max-w-2xl text-center text-[#565E6B]">
            Purpose-built for the way volunteer organizations actually work.
          </motion.p>
          <motion.div variants={stagger} {...inView} className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <motion.div key={f.title} variants={fadeUp} data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}
                className="rounded-2xl border border-[#DDE2E7] bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#5F7FA3]/40 hover:shadow-md">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8EEF4] text-[#243447]">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#243447]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#565E6B]">{f.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Demo video */}
      <section className="px-6 py-16 lg:px-12">
        <motion.div variants={stagger} {...inView} className="mx-auto max-w-4xl text-center">
          <motion.h2 variants={fadeUp} className="text-3xl font-bold tracking-tight text-[#243447]">See it in 60 seconds</motion.h2>
          <motion.p variants={fadeUp} className="mx-auto mt-3 max-w-xl text-[#565E6B]">A quick tour of how a request flows from idea to publication.</motion.p>
          <motion.div variants={fadeUp} className="mt-5 inline-flex items-center rounded-full border border-[#DDE2E7] bg-white p-1" data-testid="video-audio-toggle">
            <button onClick={() => setNarrated(true)} data-testid="video-voiceover"
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${narrated ? "bg-[#243447] text-white" : "text-[#565E6B]"}`}>🔊 With voiceover</button>
            <button onClick={() => setNarrated(false)} data-testid="video-music"
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${!narrated ? "bg-[#243447] text-white" : "text-[#565E6B]"}`}>🎵 Music only</button>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-6 overflow-hidden rounded-2xl border border-[#DDE2E7] bg-white shadow-lg">
            <video key={narrated ? "narrated" : "music"} controls poster={asset("comms_dashboard_neutral.png")} className="w-full" data-testid="home-demo-video">
              <source src={asset(narrated ? "CommConnect_demo_narrated.mp4" : "CommConnect_demo.mp4")} type="video/mp4" />
            </video>
          </motion.div>
        </motion.div>
      </section>

      {/* Screenshot gallery */}
      <section className="px-6 py-16 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <motion.div variants={stagger} {...inView} className="grid gap-6 sm:grid-cols-2">
            {SHOTS.map(([file, label]) => (
              <motion.figure key={file} variants={fadeUp} className="overflow-hidden rounded-2xl border border-[#DDE2E7] bg-white shadow-sm">
                <img src={asset(file)} alt={label} className="w-full" loading="lazy" />
                <figcaption className="border-t border-[#DDE2E7] px-5 py-3 text-sm font-medium text-[#565E6B]">{label}</figcaption>
              </motion.figure>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 lg:px-12">
        <motion.div variants={fadeUp} {...inView} className="mx-auto max-w-4xl rounded-3xl border border-[#DDE2E7] bg-[#243447] p-12 text-center shadow-xl">
          <h2 className="text-3xl font-bold tracking-tight text-white">Ready to see it for yourself?</h2>
          <p className="mx-auto mt-3 max-w-xl text-[#C7D2DE]">Jump into a fully interactive demo — explore every screen with sample data.</p>
          <button onClick={startDemo} disabled={loadingDemo} data-testid="home-demo-btn-cta"
            className="mx-auto mt-8 flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-semibold text-[#243447] transition hover:bg-[#E8EEF4] disabled:opacity-60">
            {loadingDemo ? <Loader2 className="h-5 w-5 animate-spin" /> : <PlayCircle className="h-5 w-5" />}
            Explore the Live Demo
          </button>
          <button onClick={() => navigate("/pricing")} data-testid="home-pricing-cta"
            className="mx-auto mt-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-[#C2A46D] transition hover:text-[#D4BC8C]">
            See plans & pricing <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </section>

      <footer className="border-t border-[#DDE2E7] px-6 py-10 text-center lg:px-12">
        <div className="flex items-center justify-center gap-2 text-[#243447]">
          <Radio className="h-4 w-4 text-[#5F7FA3]" />
          <span className="font-semibold">CommConnect</span>
        </div>
        <p className="mt-2 text-sm text-[#8A93A0]">One portal. Every request. Clearer communication.</p>
      </footer>
    </div>
  );
}
